import { Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence, MotionConfig, motion } from 'framer-motion'
import { Landing } from './routes/Landing'
import { Search } from './routes/Search'
import { SignIn } from './routes/SignIn'
import { Register } from './routes/Register'
import { Signup } from './routes/Signup'
import { Chat } from './routes/Chat'
import { Sell } from './routes/Sell'
import { ShareView } from './routes/ShareView'
import { RfqView } from './routes/RfqView'
import { MyShares } from './routes/MyShares'
import { Favorites } from './routes/Favorites'
import { Sourcing } from './routes/Sourcing'
import { SellerInbox } from './routes/SellerInbox'
import { SellerMyStock } from './routes/SellerMyStock'
import { SellerOrders } from './routes/SellerOrders'
import { About } from './routes/About'
import { Spotlight } from './components/Spotlight'
import { Gemma } from './components/Gemma'
import { SourcingTray } from './components/sourcing/SourcingTray'
import { Toaster } from './components/Toaster'
import { DiamondBackdrop } from './components/DiamondBackdrop'
import { Credit } from './routes/Credit'
import { Notifications } from './routes/Notifications'
import { OverdueGuard } from './components/OverdueGuard'
import { Kyc } from './routes/Kyc'
import { Devices } from './routes/Devices'
import { Admin } from './routes/Admin'
import { Terms, Privacy, Refunds } from './routes/Legal'
import { PaymentMethods } from './routes/PaymentMethods'
import { SellerIntegrations } from './routes/SellerIntegrations'
import { AccountTeam } from './routes/AccountTeam'
import { AccountMembership } from './routes/AccountMembership'
import { AccountOrg } from './routes/AccountOrg'
import { AccountProfile } from './routes/AccountProfile'
import { AccountSourcing } from './routes/AccountSourcing'
import { OrgInvite } from './routes/OrgInvite'
import { InviteWatcher } from './components/org/InviteWatcher'
import { OPEN_MARKETPLACE } from './lib/openMarketplace'
import { useIsAppMode, isInstalledApp } from './mobile/lib/appMode'
import { signupWizardInFlight } from './components/signup/SignupWizard'
import { watchAuthState } from './lib/firebase'

// Mobile app shell — lazy so the desktop bundle doesn't pay for it.
const MobileApp = lazy(() => import('./mobile/MobileApp').then((m) => ({ default: m.MobileApp })))

/**
 * Top-level router. Chat-first IA: /chat is the primary surface,
 * /search is the catalog browse fallback for users who'd rather
 * filter manually. Marketing surfaces (/, /sell, /about) hand off
 * to /chat via ?q=… for a single seamless flow.
 *
 * Page transitions: AnimatePresence wraps Routes keyed on pathname.
 * Each page mounts with a soft fade + 6px lift, exits with a quick
 * fade. Pure transform/opacity, no layout shift.
 *
 * Global keyboard shortcuts:
 *   ⌘K / Ctrl+K → toggles the Spotlight quick-ask modal
 *
 * MotionConfig reducedMotion="user" wraps the whole desktop tree so every
 * framer animation (page transitions, modals, drawers, toasts, Gemma)
 * honors the OS reduced-motion setting — same contract as the mobile shell.
 */
/** Scrolls to the top the instant it mounts. Rendered inside the keyed page
 *  wrapper so it runs when the INCOMING page mounts, not while the outgoing
 *  one is still animating out. */
function ScrollReset() {
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])
  return null
}

export default function App() {
  const location = useLocation()
  const [spotlightOpen, setSpotlightOpen] = useState(false)
  const appMode = useIsAppMode()

  // Auth state — needed only to decide whether a signed-OUT mobile-web
  // visitor at the marketing route sees the Landing instead of the auth-first
  // phone shell. `null` = not yet settled (treated as "not signed in", which is
  // safe: the worst case is a signed-in user momentarily seeing Landing at "/"
  // for one auth tick, and Landing is a valid signed-in surface anyway).
  const [signedIn, setSignedIn] = useState(false)
  useEffect(() => {
    let unsub: (() => void) | undefined
    try {
      unsub = watchAuthState((user) => setSignedIn(!!user))
    } catch { /* signed-out rendering */ }
    return () => unsub?.()
  }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const isMac = navigator.platform.toUpperCase().includes('MAC')
      const cmd = isMac ? e.metaKey : e.ctrlKey
      if (cmd && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSpotlightOpen((v) => !v)
      }
    }
    function onOpenEvt() { setSpotlightOpen(true) }
    window.addEventListener('keydown', onKey)
    window.addEventListener('aska:open-spotlight', onOpenEvt as EventListener)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('aska:open-spotlight', onOpenEvt as EventListener)
    }
  }, [])

  // App mode — render the mobile shell INSTEAD of the desktop tree.
  // (After all hooks so hook order stays stable across mode flips.)
  //
  // ONE carve-out: a SIGNED-OUT visitor in a plain mobile browser tab (narrow
  // viewport, but NOT the installed PWA / ?app=1 / app hostname) who is at the
  // marketing route "/" gets the responsive desktop Landing, not the phone
  // shell. The phone shell is auth-first — it would bounce them straight to
  // /welcome, so a first-time mobile-web visitor never saw the marketing page.
  // Landing's CTAs (/signup, /search, /chat) all live at non-"/" paths, where
  // appMode is unchanged, so tapping any of them drops them into the phone
  // shell — no dead end. Once signed in, `signedIn` flips and they get the
  // phone shell here too. `signupWizardInFlight()` is honoured (the wizard is
  // never at "/", but we exclude it explicitly so a mid-wizard account that is
  // briefly signed-out state can't flash Landing).
  const showLandingOnMobileWeb =
    appMode
    && !isInstalledApp()
    && !signedIn
    && !signupWizardInFlight()
    && location.pathname === '/'

  if (appMode && !showLandingOnMobileWeb) {
    return (
      <Suspense fallback={<div className="min-h-[100dvh] bg-cream" />}>
        <MobileApp />
      </Suspense>
    )
  }

  return (
    <MotionConfig reducedMotion="user">
      <DiamondBackdrop />
      {/* Credit-collections modal — off in the open marketplace (no credit). */}
      {!OPEN_MARKETPLACE && <OverdueGuard />}
      {/* mode="wait" is KEPT (the ScrollReset below relies on the exit
          completing before the incoming page mounts), but the timings are cut
          hard so navigation reads as instant: a ~60ms exit fade hands off to a
          ~130ms enter fade. The old 0.26s exit + 0.26s enter ran back-to-back
          under mode="wait" (~0.52s of dead air on every hop) — that was the
          "slow" feel. y motion is trimmed to a 4px settle so the page doesn't
          visibly slide. reducedMotion="user" (the MotionConfig above) already
          strips all transform/opacity motion for that preference. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.13,
            ease: [0.22, 1, 0.36, 1],
            // Exit is near-instant so the incoming page paints almost
            // immediately (mode="wait" waits for THIS to finish).
            exit: { duration: 0.06, ease: 'linear' },
          }}
        >
          {/* Reset scroll when the INCOMING page mounts (after the outgoing
              page's exit completes under mode="wait") — resetting on pathname
              change instead scrolled the still-visible exiting page to the top,
              flashing the homepage video before the new route appeared. */}
          <ScrollReset />
          <Routes location={location}>
            <Route path="/" element={<Landing />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/search" element={<Search />} />
            <Route path="/share/:id" element={<ShareView />} />
            {/* Public supplier-facing RFQ landing (agentic sourcing). No auth: a
                logged-out supplier opens the WhatsApp'd link. */}
            <Route path="/rfq/:token" element={<RfqView />} />
            <Route path="/shares" element={<MyShares />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/sourcing" element={<Sourcing />} />
            <Route path="/sell" element={<Sell />} />
            {/* The separate supplier application is DEAD (2026-08-03) — one
                supplier flow: the signup wizard with supplier preselected. */}
            <Route path="/sell/apply" element={<Navigate to="/signup?type=supplier" replace />} />
            <Route path="/seller-inbox" element={<SellerInbox />} />
            <Route path="/seller/mystock" element={<SellerMyStock />} />
            <Route path="/seller/orders" element={<SellerOrders />} />
            <Route path="/sell/integrations" element={<SellerIntegrations />} />
            <Route path="/sell/integrations/:id" element={<SellerIntegrations />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/kyc" element={<Kyc />} />
            {/* Session management. Renders an explanation (never an error) while
                DEVICE_LIMIT_LIVE is off, so a stale link is never a dead end. */}
            <Route path="/account/devices" element={<Devices />} />
            <Route path="/admin" element={<Admin />} />
            {/* Old standalone admin pages — superseded by console tabs; the
                deep-links live on as redirects into the matching tab. */}
            <Route path="/admin/analytics" element={<Navigate to="/admin?tab=analytics" replace />} />
            <Route path="/admin/chat-history" element={<Navigate to="/admin?tab=chat" replace />} />
            <Route path="/admin/kb" element={<Navigate to="/admin?tab=kb" replace />} />
            <Route path="/notifications" element={<Notifications />} />
            {/* v0 directory has NO on-platform buyer orders — checkout/cart are
                gone and Stripe is membership-only. The buyer order pages are
                retired (the only order flow that survives is the Aska+ concierge
                WhatsApp+tracking coordination, admin-side). A stray /orders link
                (bookmark, old email) lands on the profile, never a 404. */}
            <Route path="/orders" element={<Navigate to="/account/profile" replace />} />
            <Route path="/orders/:orderId" element={<Navigate to="/account/profile" replace />} />
            {/* Org accounts. Every one of these renders "not available here"
                (never an error) while ORGS_LIVE is off, because the whole /org
                router 404s before requireAuth. /account/team is NOT a free
                choice of path: functions/src/billing/index.ts hardcodes it as
                the Stripe return URL, and a 404 there would dump a customer
                who has just been charged onto the home page. */}
            <Route path="/account/team" element={<AccountTeam />} />
            {/* Aska+ membership. `/account/membership` is the Stripe return URL
                hardcoded in functions/src/billing/askaPlus.ts — a 404 here would
                dump a buyer who has just JOINED onto the home page. */}
            <Route path="/account/membership" element={<AccountMembership />} />
            <Route path="/account/company" element={<AccountOrg />} />
            <Route path="/account/profile" element={<AccountProfile />} />
            {/* Profile → My sourcing (agentic-sourcing monitoring). Renders the
                inert pitch off-flag, so a deep link never dead-ends. */}
            <Route path="/account/sourcing" element={<AccountSourcing />} />
            <Route path="/invite" element={<OrgInvite />} />
            {/* /credit serves BOTH modes now: broker net-terms AND the open-
                marketplace "Pay with Aska Credit" financing (Revenue Stream 3,
                #64) — the page self-gates on the buyer's financing standing +
                the server CREDIT_FINANCING_LIVE flag, so it's inert until armed. */}
            <Route path="/credit" element={<Credit />} />
            {!OPEN_MARKETPLACE && (
              <>
                <Route path="/payment-methods" element={<PaymentMethods />} />
              </>
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </motion.div>
      </AnimatePresence>

      {/* Carries an emailed ?invite= token to /invite for a signed-in user —
          /signup bounces them to the catalog, which for an invitee is a silent
          dead end. Inert without a token, i.e. always, until ORGS_LIVE. */}
      <InviteWatcher />
      <Spotlight open={spotlightOpen} onClose={() => setSpotlightOpen(false)} />
      {/* Gemma — always-present floating AI assistant. Hides itself on
          /chat + /admin/* (see isHiddenPath). Complements Cmd-K + /chat. */}
      <Gemma />
      {/* Persistent Sourcing-list tray — self-gates to nothing unless the RFQ
          surface is armed (flag/preview) AND the list is non-empty. */}
      <SourcingTray />
      <Toaster />
    </MotionConfig>
  )
}
